import { Fragment, cloneElement, useMemo, useRef } from 'react';
import type { DialogProps } from '@mui/material';
import Button from '@mui/material/Button';
import CardContent from '@mui/material/CardContent';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import CardHeader from '@mui/material/CardHeader';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import { ModalActionButton } from 'interfaces/components';
import { getOppositeColor } from 'helpers/utils';

/**
 * Componente GenericModal
 *
 * Modal reutilizable con acciones y contenido personalizable.
 *
 * @component
 * @param {object} props - Props del componente.
 * @param {ModalActionButton[]} [props.actions] - Acciones personalizadas para el modal.
 * @param {React.ReactNode} [props.bodyContent] - Contenido principal del modal.
 * @param {React.ReactElement} [props.children] - Elemento hijo, generalmente un formulario.
 * @param {DialogProps} props.dialogProps - Props del componente Dialog de MUI.
 * @param {boolean} [props.disableSubmitButton] - Deshabilita el botón de submit.
 * @param {React.ReactNode} [props.headerContent] - Contenido adicional en el encabezado.
 * @param {boolean} [props.hideButtonAccept] - Oculta el botón de aceptar.
 * @param {boolean} [props.hideButtonCancel] - Oculta el botón de cancelar.
 * @param {boolean} [props.isMutating] - Indica si hay una acción en curso.
 * @param {string} [props.className] - Clase CSS personalizada.
 * @param {() => void} [props.onCancelClickButton] - Callback al cancelar.
 * @param {(data: EventListener) => void} [props.onSubmitClickButton] - Callback al aceptar.
 * @param {React.CSSProperties} [props.stylesBodyContent] - Estilos para el contenido.
 * @param {string} [props.submitText] - Texto del botón de submit.
 * @example
 * <GenericModal dialogProps={{ open: true, onClose: () => {} }} />
 */
type Props = {
  actions?: ModalActionButton[];
  bodyContent?: React.ReactNode;
  children?: React.ReactElement;
  dialogProps: DialogProps;
  disableSubmitButton?: boolean;
  headerContent?: React.ReactNode;
  hideButtonAccept?: boolean;
  hideButtonCancel?: boolean;
  isMutating?: boolean;
  className?: string;
  onCancelClickButton?: () => void;
  onSubmitClickButton?: (data: EventListener) => void;
  stylesBodyContent?: React.CSSProperties;
  submitText?: string;
};

function GenericModal({
  actions,
  bodyContent,
  children,
  dialogProps,
  disableSubmitButton = false,
  headerContent,
  hideButtonAccept = false,
  hideButtonCancel = false,
  isMutating = false,
  onCancelClickButton,
  onSubmitClickButton,
  className,
  stylesBodyContent,
  submitText = ''
}: Props) {
  const formRef = useRef<HTMLFormElement>(null);
  const { t } = useTranslation('global');

  let addedForm: React.ReactElement | undefined;

  if (children) {
    // Si hay onSubmitClickButton, no sobrescribir el ref del children
    // para permitir que componentes con forwardRef funcionen correctamente
    if (onSubmitClickButton) {
      addedForm = children;
    } else {
      addedForm = cloneElement(children, {
        ref: formRef
      });
    }
  }

  function handleSubmitEvent() {
    if (formRef.current)
      formRef.current?.dispatchEvent(
        new Event('submit', { cancelable: true, bubbles: true })
      );
  }

  const renderButtons: ModalActionButton[] = useMemo<ModalActionButton[]>(
    () => [
      {
        backgroundColor: '#f7fdff',
        text: t('common.commonActions.cancel'),
        variant: 'outlined',
        opacity: 0.7,
        hideBtnCancel: hideButtonCancel,
        handle: onCancelClickButton
      },
      ...(actions || []),
      ...(addedForm || onSubmitClickButton
        ? [
            {
              backgroundColor: '#1D6FCC',
              text: submitText || t('common.commonActions.accept'),
              variant: 'contained',
              hideBtnAccept: hideButtonAccept,
              handle: onSubmitClickButton || handleSubmitEvent,
              ...(addedForm && {
                form: addedForm.props.id,
                type: 'submit'
              })
            } as ModalActionButton
          ]
        : [])
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onCancelClickButton, onSubmitClickButton, handleSubmitEvent]
  );

  return (
    <Dialog
      alt="none"
      maxWidth="sm"
      scroll="body"
      fullWidth
      {...dialogProps}
      onClose={isMutating ? undefined : onCancelClickButton}
      disableEscapeKeyDown
    >
      <DialogContent sx={{ padding: '10px 15px' }}>
        <DialogTitle sx={{ m: 0, p: 1 }}>
          {onCancelClickButton ? (
            <IconButton
              aria-label="close"
              disabled={isMutating}
              onClick={onCancelClickButton}
              sx={{
                position: 'absolute',
                right: 0,
                top: '-3px',
                color: (theme) => theme.palette.grey[500]
              }}
            >
              <CloseIcon />
            </IconButton>
          ) : null}
        </DialogTitle>
        <CardHeader
          subheader={
            dialogProps?.title && (
              <Typography
                color="#063468"
                fontSize="14px"
                fontWeight="500"
                textAlign="center"
                whiteSpace="pre-wrap"
              >
                {dialogProps?.title}
              </Typography>
            )
          }
        />
        {headerContent && (
          <CardHeader
            title={
              <Typography
                color="#063468"
                fontSize="20px"
                fontWeight="400"
                marginX={8}
                textAlign="center"
                whiteSpace="pre-wrap"
              >
                {headerContent}
              </Typography>
            }
          />
        )}
        <CardContent
          className={className || ''}
          sx={{ ...stylesBodyContent, padding: 2, wordWrap: 'break-word' }}
        >
          {bodyContent}
          {addedForm}
        </CardContent>
      </DialogContent>
      <DialogActions sx={{ paddingX: '24px' }}>
        <Stack
          direction="row"
          gap={2}
          justifyContent="space-between"
          marginBottom="1em"
          width="100%"
        >
          {renderButtons?.map(
            ({
              backgroundColor,
              opacity,
              text,
              variant,
              handle,
              hideBtnCancel,
              hideBtnAccept,
              form,
              type
            }) =>
              hideBtnCancel || hideBtnAccept ? (
                <Fragment key={text} />
              ) : (
                <Button
                  disabled={
                    type === 'submit'
                      ? isMutating || disableSubmitButton
                      : isMutating
                  }
                  form={form}
                  key={text}
                  onClick={handle}
                  size="large"
                  sx={{
                    width: renderButtons?.length > 1 ? '100%' : '150px',
                    opacity: opacity || 1,
                    backgroundColor,
                    color: getOppositeColor(backgroundColor),
                    marginX: 1,
                    ':hover': {
                      backgroundColor
                    }
                  }}
                  type={type}
                  variant={variant || 'contained'}
                >
                  {text}
                </Button>
              )
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

export default GenericModal;
